// Price formatting for the package picker. Extracted from SymphonyPricing so the coupon
// preview (lib/coupon.ts, components/pricing/) renders amounts identically to the plan cards.

import type { Cadence, PickerOption } from './plans';

// Symbol + which side of the amount it goes on, per the picker's ISO currency (from GET /api/plans).
//
// **Position is part of the currency, not a style choice.** The dollar family, the pound and the euro
// lead; every currency we price in central/northern Europe trails — `449 Kč`, `75 zł`, `6 490 Ft`,
// `199 kr`, `90 lei`. Rendering `zł75` is not a near-miss, it is wrong in the way a misspelling is,
// and it is the first thing a local buyer sees.
//
// CAD keeps its `CA$` qualifier rather than a bare `$` — a Canadian seeing `$100` cannot tell which
// dollars they are being charged, and the two differ by about a third.
//
// **DKK and SEK are both written `kr`, and that is fine here.** The picker resolves ONE currency for
// the whole page (`/api/plans` returns a single top-level `currency`), so a Danish buyer only ever
// sees krone and a Swedish buyer only ever sees krona — the two never appear side by side for the
// ambiguity to bite. Do not "disambiguate" them into `DKK`/`SEK` codes: that would show every Nordic
// buyer an accountant's notation instead of their own currency.
const CURRENCIES: Record<string, { symbol: string; suffix?: true }> = {
  usd: { symbol: '$' },
  cad: { symbol: 'CA$' },
  gbp: { symbol: '£' },
  eur: { symbol: '€' },
  czk: { symbol: 'Kč', suffix: true },
  dkk: { symbol: 'kr', suffix: true },
  huf: { symbol: 'Ft', suffix: true },
  pln: { symbol: 'zł', suffix: true },
  ron: { symbol: 'lei', suffix: true },
  sek: { symbol: 'kr', suffix: true },
  // The shekel sign LEADS the amount here, matching the VMR checkout formatter's existing `ils` entry —
  // the disclosure under Stripe's Subscribe button and this card must not disagree on the same price.
  ils: { symbol: '\u20aa' },
  // Leads, and the rupee sign is unambiguous.
  //
  // `brl: 'R$'` sat here from before Brazil was priced and is now GONE: Brazil is billed in US dollars
  // (see picker-geo), no BRL price has ever existed in the catalog, and no charge has ever been in
  // reais — so the entry was unreachable by every path, since a currency only arrives here off a
  // resolved Stripe price. It was kept for one revision on a "ready for the day Stripe Tax covers
  // Brazil" argument, which is not how any other currency got here: each was added in the same change
  // that opened its market. An unreachable row is worse than a missing one — it implies we price in
  // reais, which is the exact thing the Brazil work had to un-say.
  inr: { symbol: '\u20b9' },
  // Qualified for the same reason as CAD: an Australian seeing a bare `$100` cannot tell which dollars.
  aud: { symbol: 'A$' },
  // `NZ$` for the same reason, and here the ambiguity is not hypothetical: NZD and AUD trade about a
  // tenth apart, so an unqualified `$` is likeliest to be misread as the neighbour's dollar precisely
  // where the two markets sit side by side.
  nzd: { symbol: 'NZ$' },
  // The hryvnia sign trails the amount in Ukrainian usage ("1 000 ₴").
  uah: { symbol: '₴', suffix: true },
  // The tenge sign trails, as Kazakh usage writes it ("10 710 ₸"). Two decimals — KZT is NOT one of
  // Stripe's zero-decimal currencies, so the amount arriving here is already divided and needs no
  // entry in common-lib's no-division set (the trap ISK falls into from the other direction).
  kzt: { symbol: '₸', suffix: true },
  // The yen sign leads, like the dollar family. Note JPY is zero-decimal, which is a MINOR-UNIT
  // concern handled in common-lib `minorToMajorUnits`, not a formatting one — by the time an amount
  // reaches here it is already whole yen.
  jpy: { symbol: '¥' },
  // **CHF and AED write their ISO CODE, and the code takes a space** — `CHF 18`, `AED 65`. Neither
  // market leads with a glyph: Swiss prices are written `CHF`/`Fr.` and the dirham's `د.إ` is Arabic
  // script, which reads as a foreign object dropped into an LTR price. The separator is baked into
  // the symbol as a NON-BREAKING space rather than added by `formatMoney`, because the card headline
  // (`PriceRow`) lays the amount out itself off `currencySymbol` + `currencySuffixed` and would not
  // see a separate flag — a space in the symbol is the one form BOTH paths honour.
  chf: { symbol: 'CHF\u00A0' },
  aed: { symbol: 'AED\u00A0' },
  // The THIRD currency written `kr`, after DKK and SEK — and harmless for the same reason: the picker
  // resolves ONE currency per page, so a Norwegian buyer only ever sees krone. Trails, as in Norway.
  nok: { symbol: 'kr', suffix: true },
  // Qualified like `CA$`/`A$`/`NZ$` — a bare `$` on a Singapore price reads as US dollars.
  sgd: { symbol: 'S$' },
  // The rand's `R` leads and needs no qualifier: no other currency we price is written `R` alone
  // (`R$` is the real, and it carries the `$`).
  zar: { symbol: 'R' },
  // The won leads. **KRW is the SECOND Stripe no-division currency we price, after JPY** — a Stripe
  // amount of `30000` already IS ₩30,000. That is a minor-unit concern, not a formatting one, and it is
  // already handled: `krw` is in common-lib's `STRIPE_NO_DIVISION_CURRENCIES`, so an amount arriving
  // here is whole won. Any NEW display path that divides by a hardcoded 100 breaks Korea and Japan
  // together, 100× too low and silently.
  krw: { symbol: '₩' },
  // Qualified like `CA$`/`A$`/`NZ$`/`S$`: `NT$` is how a Taiwan dollar is written, and a bare `$` on a
  // Taipei price reads as US dollars — a ~30× understatement, the widest of the dollar family.
  // ⚠️ TWD is zero-decimal for PAYOUTS only, so a charge amount is an ordinary ×100 value. It must stay
  // OUT of the no-division set; putting it in renders every Taiwanese price 100× too high.
  twd: { symbol: 'NT$' },
  // The baht sign leads, as in Thai retail usage.
  thb: { symbol: '฿' },
  // MY / KE / IS / VN. `RM` and `KSh` lead, as both are written locally; neither collides with
  // anything here (`R` alone is ZAR, `R$` is gone with BRL).
  myr: { symbol: 'RM' },
  kes: { symbol: 'KSh' },
  // The FOURTH currency written `kr`, after DKK (dropped), SEK and NOK — safe for the same reason:
  // the picker resolves ONE currency per page, so an Icelandic buyer only ever sees krona.
  //
  // ⚠️ ISK is a zero-decimal CURRENCY whose Stripe amounts are still x100 ("to charge 5 UGX provide
  // 500"), so it is deliberately ABSENT from common-lib STRIPE_NO_DIVISION_CURRENCIES. Putting it in
  // renders every Icelandic price 1/100th. That is a minor-unit concern, not a formatting one — by the
  // time an amount reaches here it is already whole krona.
  isk: { symbol: 'kr', suffix: true },
  // The dong sign trails, as in Vietnamese usage ("600.000 ₫").
  //
  // ⚠️ VND is the OPPOSITE case to ISK and IS in the no-division set: a Stripe VND amount is already
  // whole dong. The two sit next to each other here on purpose — they are the pair most likely to be
  // conflated, and getting either backwards is a silent 100x in one market.
  vnd: { symbol: '₫', suffix: true },
  // GE/CL/TR/NG/PE/TZ, added in the SAME change as the harness `PRICE_SYMBOLS`. When those two tables
  // drift, the agent quotes one symbol and the card shows another — that is the `nzd` bug. The lari
  // trails, as Georgian convention writes it; the lira, naira and sol lead; alphabetic `TSh` leads
  // with a non-breaking separator.
  //
  // ⚠️ **`clp` writes its ISO code, NOT `$`.** The Chilean peso is a dollar-sign currency, and a bare
  // `$` on a CLP amount is the worst mis-quote in this table: CLP 19,000 is about US$20, so `$19,000`
  // reads as roughly 950x the real price. It joins CHF/AED on a non-breaking-space ISO prefix rather
  // than the `CA$`/`A$`/`NZ$`/`S$`/`NT$` qualifier family, because there is no conventional short form
  // for it and `CL$` would invite exactly the misreading it is there to stop.
  gel: { symbol: '₾', suffix: true },
  clp: { symbol: 'CLP\u00A0' },
  try: { symbol: '₺' },
  ngn: { symbol: '₦' },
  pen: { symbol: 'S/\u00A0' },
  tzs: { symbol: 'TSh\u00A0' },
  // MX, added in the SAME change as the harness `PRICE_SYMBOLS` — the `nzd` rule, and `kzt` is the
  // reason it is stated twice: that one went in here alone and the agent quoted `$10710` for a tenge
  // plan until the harness caught up.
  //
  // ⚠️ **`mxn` is qualified `MX$`, never a bare `$`.** The Mexican peso is a dollar-sign currency, so
  // `$340` reads as US$340 — 17x the real price. It joins the `CA$`/`A$`/`NZ$`/`S$`/`NT$` family rather
  // than CLP's ISO-code treatment because `MX$` IS the conventional short form, which `CL$` is not.
  mxn: { symbol: 'MX$' },
};

const fallback = CURRENCIES.usd;
const entry = (currency?: string) => CURRENCIES[(currency ?? 'usd').toLowerCase()] ?? fallback;

/** The bare symbol, for a call site that lays out the amount itself. Prefer `formatMoney`, which also
 *  knows which side the symbol belongs on — a caller that concatenates this itself will get a trailing
 *  currency wrong. */
export function currencySymbol(currency?: string): string {
  return entry(currency).symbol;
}

/** Whether this currency's symbol trails the amount. Exported for the card headline (`PriceRow`), which
 *  renders the amount ungrouped in its own 52px type run and so cannot use `formatMoney`'s single
 *  string — it still has to place the symbol on the correct side, and asks here. */
export function currencySuffixed(currency?: string): boolean {
  return entry(currency).suffix === true;
}

// A whole-unit amount with its symbol on the correct side, e.g. "£360" / "$40" / "449 Kč".
// The trailing form takes a NON-BREAKING space so an amount never wraps away from its currency
// mid-line — "6 490" on one line and "Ft" on the next reads as a different number.
export function formatMoney(amount: number, currency?: string): string {
  const { symbol, suffix } = entry(currency);
  const value = amount.toLocaleString();
  return suffix ? `${value}\u00A0${symbol}` : `${symbol}${value}`;
}

// The charge amount for an option at a cadence (the raw per-cadence price the backend classifies
// a switch on: a yearly amount vs a monthly amount). undefined when that cadence isn't priced.
export function amountFor(option: PickerOption | undefined, cadence: Cadence): number | undefined {
  if (!option) return undefined;
  return cadence === 'yearly' ? option.yearlyAmount : option.monthlyAmount;
}

// A possibly-FRACTIONAL amount with its symbol on the correct side, e.g. "$8.33" / "6.67 zł".
//
// `formatMoney` above is for whole units and leans on a bare `toLocaleString()`, which caps at three
// fraction digits — fine for a price, wrong for a rate: $8.3333 would render "$8.333". This caps at
// two and drops them when the amount is whole, so the same call is "$8.33" in the US and "¥1,500" in
// Japan. A forced "¥1,500.00" is not how a yen amount is ever written, and JPY/KRW/VND are markets
// this screen serves.
export function formatRate(amount: number, currency?: string): string {
  const { symbol, suffix } = entry(currency);
  const value = amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
  // Escaped, not literal — a literal NBSP in source trips `no-irregular-whitespace`.
  return suffix ? `${value}\u00A0${symbol}` : `${symbol}${value}`;
}
